import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useMeasure, useScroll, useWindowSize } from 'react-use';
import '../../assets/icons/arrowRight.svg.mjs';
import { useHorizontalScroll } from '../../hooks/useHorizontalScroll.mjs';
import { FADE_BUTTON_SIZE, OuterContainer, ScrollContainer, Item, LeftArrowIcon, ArrowIconSvg, RightArrowIcon } from './ScrollWrapper.styles.mjs';
import ArrowRightIcon__default from "../../assets/arrowRight.svg";
"use strict";
const SCROLL_CONTAINER_ITEM_CLASS_NAME = "scroll-container-item";
const NEAR_BOUNDARY_WIDTH = 10;
const ScrollWrapper = function ScrollWrapper2({
  align,
  direction,
  items,
  fillType,
  fillColor,
  accentColor,
  arrowColor,
  scrollStepType = "scrollScreen",
  gap,
  className,
  arrowFadeColor,
  activeIndex,
  circleArrowBackground,
  arrowGap,
  gradientSize,
  hideArrows,
  onScrollChange,
  enableWheelScroll,
  arrowIcon,
  draggable,
  alignActive = "center",
  disableScrollToActive
}) {
  const containerRef = useRef(null);
  const [containerMeasureRef, containerSize] = useMeasure();
  useHorizontalScroll(containerRef, !!enableWheelScroll);
  useEffect(() => {
    if (containerRef.current) {
      containerMeasureRef(containerRef.current);
    }
  }, [containerMeasureRef]);
  const getContainerItemWidth = useCallback(() => {
    const containerItem = containerRef.current?.querySelector(`.${SCROLL_CONTAINER_ITEM_CLASS_NAME}`);
    if (!containerItem) {
      return 0;
    }
    return containerItem.getBoundingClientRect().width;
  }, []);
  const getInvisibleItemOffset = useCallback(({
    scrollDirection
  }) => {
    if (!containerRef.current) {
      return 0;
    }
    const container = containerRef.current;
    const items2 = container.querySelectorAll(`.${SCROLL_CONTAINER_ITEM_CLASS_NAME}`);
    const containerRect = container.getBoundingClientRect();
    const isHorizontal = direction === "horizontal";
    const fadeSize = 20;
    if (isHorizontal) {
      const padding = scrollDirection === 1 ? parseFloat(window.getComputedStyle(container)["paddingRight"]) : parseFloat(window.getComputedStyle(container)["paddingLeft"]);
      const offset = Math.max(padding, fadeSize);
      const containerEdgeWithoutPadding = scrollDirection === 1 ? containerRect["right"] - offset : containerRect["left"] + offset;
      if (scrollDirection === 1) {
        for (const item of Array.from(items2)) {
          const itemRect = item.getBoundingClientRect();
          if (itemRect.right > containerEdgeWithoutPadding) {
            return Math.abs(itemRect.right - containerEdgeWithoutPadding);
          }
        }
      } else {
        for (let i = items2.length - 1; i >= 0; i--) {
          const item = items2[i];
          if (item) {
            const itemRect = item.getBoundingClientRect();
            if (itemRect.left < containerEdgeWithoutPadding) {
              return Math.abs(containerEdgeWithoutPadding - itemRect.left);
            }
          }
        }
      }
    } else {
      const padding = scrollDirection === 1 ? parseFloat(window.getComputedStyle(container)["paddingBottom"]) : parseFloat(window.getComputedStyle(container)["paddingTop"]);
      const offset = Math.max(padding, fadeSize);
      const containerEdgeWithoutPadding = scrollDirection === 1 ? containerRect["bottom"] - offset : containerRect["top"] + offset;
      if (scrollDirection === 1) {
        for (const item of Array.from(items2)) {
          const itemRect = item.getBoundingClientRect();
          if (itemRect.bottom > containerEdgeWithoutPadding) {
            return Math.abs(itemRect.bottom - containerEdgeWithoutPadding);
          }
        }
      } else {
        for (let i = items2.length - 1; i >= 0; i--) {
          const item = items2[i];
          if (item) {
            const itemRect = item.getBoundingClientRect();
            if (itemRect.top < containerEdgeWithoutPadding) {
              return Math.abs(containerEdgeWithoutPadding - itemRect.top);
            }
          }
        }
      }
    }
    return 0;
  }, [direction]);
  const getVisibleItemsWidth = useCallback(() => {
    if (!containerRef.current) {
      return 0;
    }
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerItems = containerRef.current.querySelectorAll(`.${SCROLL_CONTAINER_ITEM_CLASS_NAME}`);
    let visibleItemsWidth = 0;
    containerItems.forEach(item => {
      const itemRect = item.getBoundingClientRect();
      if (direction === "horizontal" && itemRect.left >= containerRect.left && itemRect.right <= containerRect.right || direction === "vertical" && itemRect.top >= containerRect.top && itemRect.bottom <= containerRect.bottom) {
        if (visibleItemsWidth !== 0 && gap !== void 0) {
          visibleItemsWidth += gap;
        }
        visibleItemsWidth += direction === "horizontal" ? itemRect.width : itemRect.height;
      }
    });
    return visibleItemsWidth;
  }, [direction, gap]);
  const getActiveIndexOffset = useCallback(index => {
    const offset = {
      left: void 0,
      top: void 0
    };
    if (!containerRef.current) {
      return offset;
    }
    const containerRect = containerRef.current.getBoundingClientRect();
    const actNav = containerRef.current.children[index];
    if (actNav) {
      const actNavRect = actNav.getBoundingClientRect();
      if (direction === "horizontal") {
        if (alignActive === "left") {
          if (actNavRect.left < containerRect.left || actNavRect.right > containerRect.right) {
            offset.left = actNavRect.left - containerRect.left + containerRef.current.scrollLeft;
          }
        } else {
          if (actNavRect.right > containerRect.right) {
            offset.left = actNavRect.right - containerRect.right + containerRef.current.scrollLeft + (!hideArrows ? FADE_BUTTON_SIZE : 0) + containerRect.width / 2 - actNavRect.width / 2;
          } else if (actNavRect.left < containerRect.left) {
            offset.left = actNavRect.left - containerRect.left + containerRef.current.scrollLeft - (!hideArrows ? FADE_BUTTON_SIZE : 0) - containerRect.width / 2 + actNavRect.width / 2;
          }
        }
      } else if (direction === "vertical") {
        if (actNavRect.bottom > containerRect.bottom) {
          offset.top = actNavRect.bottom - containerRect.bottom + containerRef.current.scrollTop + (!hideArrows ? FADE_BUTTON_SIZE : 0);
        } else if (actNavRect.top < containerRect.top) {
          offset.top = actNavRect.top - containerRect.top + containerRef.current.scrollTop + (!hideArrows ? FADE_BUTTON_SIZE : 0);
        }
      }
    }
    return offset;
  }, [direction, alignActive, hideArrows]);
  const {
    x: refScrollX,
    y: refScrollY
  } = useScroll(
  // HACK: Type issue with ref on useScroll side
  containerRef);
  const windowSize = useWindowSize();
  const [scrollX, setScrollX] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    if (!containerRef.current) return;
    const initScrollX = containerRef.current.scrollLeft;
    const initScrollY = containerRef.current.scrollTop;
    setScrollX(initScrollX);
    setScrollY(initScrollY);
    onScrollChange?.({
      scrollX: initScrollX,
      scrollY: initScrollY,
      scrollWidth: containerRef.current.scrollWidth
    });
  }, [onScrollChange]);
  useEffect(() => {
    setScrollX(refScrollX);
    setScrollY(refScrollY);
    onScrollChange?.({
      scrollX: refScrollX,
      scrollY: refScrollY,
      scrollWidth: containerRef.current?.scrollWidth ?? 0
    });
  }, [
  // HACK: needed windowSize to retrigger the effect
  windowSize,
  // #############################################################################
  onScrollChange, refScrollX, refScrollY]);
  const [showFadeStart, setShowFadeStart] = useState(false);
  const [showFadeEnd, setShowFadeEnd] = useState(false);
  useEffect(() => {
    if (!containerRef.current) return;
    const width = containerRef.current.offsetWidth;
    const height = containerRef.current.offsetHeight;
    const scrollWidth = containerRef.current.scrollWidth;
    const scrollHeight = containerRef.current.scrollHeight;
    const scrollXFixOffset = 1;
    switch (direction) {
      case "horizontal":
        setShowFadeStart(scrollX > 0);
        setShowFadeEnd(Math.ceil(scrollX + scrollXFixOffset) < scrollWidth - width);
        break;
      case "vertical":
        setShowFadeStart(scrollY > 0);
        setShowFadeEnd(Math.ceil(scrollY + scrollXFixOffset) < scrollHeight - height);
        break;
    }
  }, [
  // HACK: needed windowSize, containerSize.width to retrigger the effect
  containerSize.width, windowSize,
  // #############################################################################
  direction, scrollX, scrollY, items]);
  const handleArrowClick = useCallback(first => {
    const container = containerRef.current;
    if (!container) return;
    const scrollDirection = first ? -1 : 1;
    const {
      scrollWidth,
      scrollHeight,
      offsetWidth,
      offsetHeight,
      clientWidth
    } = container;
    const isHorizontal = direction === "horizontal";
    const scrollPosition = isHorizontal ? scrollX : scrollY;
    const scrollSize = isHorizontal ? scrollWidth : scrollHeight;
    let scrollStep = 0;
    switch (scrollStepType) {
      case "scrollScreen":
        scrollStep = isHorizontal ? offsetWidth : offsetHeight;
        break;
      case "itemWidth":
        scrollStep = getContainerItemWidth();
        break;
      case "visibleItemsWidth":
        scrollStep = getVisibleItemsWidth();
        break;
      case "nextItem":
        scrollStep = getInvisibleItemOffset({
          scrollDirection
        });
        break;
    }
    const newScrollPosition = scrollPosition + scrollDirection * (scrollStep + (gap ?? 0));
    const newScrollPositionWithinBounds = Math.max(0, Math.min(scrollSize - scrollStep, newScrollPosition));
    const possibleScroll = scrollSize - clientWidth;
    const nearBoundary = NEAR_BOUNDARY_WIDTH;
    if (newScrollPosition < nearBoundary || newScrollPosition < 0) {
      container.scrollTo({
        left: 0,
        top: 0,
        behavior: "smooth"
      });
      return;
    }
    if (possibleScroll - newScrollPosition < nearBoundary || newScrollPosition > possibleScroll) {
      container.scrollTo({
        left: possibleScroll,
        top: possibleScroll,
        behavior: "smooth"
      });
      return;
    }
    container.scrollTo({
      left: isHorizontal ? newScrollPositionWithinBounds : void 0,
      top: !isHorizontal ? newScrollPositionWithinBounds : void 0,
      behavior: "smooth"
    });
  }, [direction, scrollX, scrollY, scrollStepType, gap, getContainerItemWidth, getVisibleItemsWidth, getInvisibleItemOffset]);
  useEffect(() => {
    window.setTimeout(() => {
      if (!containerRef.current || disableScrollToActive) return;
      if (activeIndex !== void 0 && activeIndex > -1) {
        const offset = getActiveIndexOffset(activeIndex);
        if (offset.left !== void 0) {
          containerRef.current.scrollLeft = offset.left;
        }
        if (offset.top !== void 0) {
          containerRef.current.scrollTop = offset.top;
        }
      } else {
        containerRef.current.scrollTo({
          left: 0,
          top: 0,
          behavior: "smooth"
        });
      }
    }, 0);
  }, [activeIndex, getActiveIndexOffset, disableScrollToActive]);
  const arrowIconSrc = useMemo(() => {
    const icon = arrowIcon?.icon ?? ArrowRightIcon__default;
    return typeof icon === "string" ? icon : icon.src;
  }, [arrowIcon]);
  useEffect(() => {
    if (!draggable || !containerRef.current) return;
    const container = containerRef.current;
    let isDragging = false;
    let hasDragged = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;
    const onPointerDown = e => {
      if (e.pointerType !== "mouse" || e.buttons !== 1) return;
      isDragging = true;
      hasDragged = false;
      startX = e.pageX - container.offsetLeft;
      startY = e.pageY - container.offsetTop;
      scrollLeft = container.scrollLeft;
      scrollTop = container.scrollTop;
      document.body.style.cursor = "grabbing";
      container.style.cursor = "grabbing";
    };
    const onPointerMove = e => {
      if (e.pointerType !== "mouse") return;
      if (!isDragging) return;
      e.preventDefault();
      hasDragged = true;
      const walkMultiplier = 1;
      const x = e.pageX - container.offsetLeft;
      const y = e.pageY - container.offsetTop;
      const walkX = (x - startX) * walkMultiplier;
      const walkY = (y - startY) * walkMultiplier;
      container.scrollLeft = scrollLeft - walkX;
      container.scrollTop = scrollTop - walkY;
    };
    const onPointerUp = e => {
      if (e.pointerType !== "mouse") return;
      isDragging = false;
      document.body.style.cursor = "unset";
      container.style.cursor = "unset";
    };
    const onVisibilityChange = () => {
      isDragging = false;
      document.body.style.cursor = "unset";
      container.style.cursor = "unset";
      hasDragged = false;
    };
    const onClick = e => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    container.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("visibilitychange", onVisibilityChange);
    container.querySelectorAll("a").forEach(anchor => {
      anchor.addEventListener("click", onClick);
    });
    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      container.querySelectorAll("a").forEach(anchor => {
        anchor.removeEventListener("click", onClick);
      });
    };
  }, [draggable]);
  return /* @__PURE__ */jsxs(OuterContainer, {
    $align: align,
    $direction: direction,
    $showFadeStart: showFadeStart,
    $showFadeEnd: showFadeEnd,
    $fillType: fillType,
    $fillColor: fillColor,
    $gradientSize: gradientSize,
    className,
    children: [/* @__PURE__ */jsx(ScrollContainer, {
      ref: containerRef,
      $direction: direction,
      $gap: gap,
      children: items.map((item, i) => /* @__PURE__ */jsx(Item, {
        className: SCROLL_CONTAINER_ITEM_CLASS_NAME,
        $draggable: !!draggable,
        children: item.render()
      }, item.id + i))
    }), /* @__PURE__ */jsx(LeftArrowIcon, {
      $show: showFadeStart && !hideArrows,
      $first: true,
      $fillType: fillType,
      $fillColor: fillColor,
      $accentColor: accentColor,
      $direction: direction,
      $arrowFadeColor: arrowFadeColor,
      $circleArrowBackground: circleArrowBackground,
      onClick: () => handleArrowClick(true),
      $arrowGap: arrowGap,
      children: /* @__PURE__ */jsx(ArrowIconSvg, {
        $arrowColor: arrowColor,
        width: arrowIcon?.width ?? (circleArrowBackground ? 10 : 8),
        height: arrowIcon?.height ?? (circleArrowBackground ? 16 : 13),
        src: arrowIconSrc
      })
    }), /* @__PURE__ */jsx(RightArrowIcon, {
      $show: showFadeEnd && !hideArrows,
      $fillType: fillType,
      $fillColor: fillColor,
      $accentColor: accentColor,
      $direction: direction,
      $arrowFadeColor: arrowFadeColor,
      $circleArrowBackground: circleArrowBackground,
      onClick: () => handleArrowClick(false),
      $arrowGap: arrowGap,
      children: /* @__PURE__ */jsx(ArrowIconSvg, {
        $arrowColor: arrowColor,
        width: arrowIcon?.width ?? (circleArrowBackground ? 10 : 8),
        height: arrowIcon?.height ?? (circleArrowBackground ? 16 : 13),
        src: arrowIconSrc
      })
    })]
  });
};
export { ScrollWrapper };
//# sourceMappingURL=ScrollWrapper.mjs.map
